import React, { useCallback, useRef, useState, useReducer } from 'react';
import CcmClinicalNotesModal from 'components/templates/ccm/patientDetails/components/narrativeTemplates/modal';
import DeviceTroubleshootModals from 'components/organisms/device-troubleshoot-templates';
import { deleteUnnecessaryEmptyLines } from 'components/organisms/device-troubleshoot-templates/helpers';
import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { usePatientNextAppointment } from 'infrastructure/hooks/patient/use-patient-next-appointment';
import { debug } from 'utils/logger';
import CkdCcmClinicalModal from 'components/organisms/ckd-clinical-note-modal';
import InitialApcmTemplateModal from 'components/organisms/apcm-templates-modal/initial-template';
import MonthlyApcmTemplateModal from 'components/organisms/apcm-templates-modal/monthly-template';
import { useGetRpmInteractionTime } from 'components/organisms/interactiveTime/hooks';

import ClinicalNotesModalForm from '../admin-panel/forms/clinical-notes';
import CcmInteractionStatusModal from '../ccmInteractionStatus/modal';
import { useLogic } from './logic';

import type { NextAppointment } from 'infrastructure/classes/next-appointment';

const RPM_80_MINUTES = 4_800;
interface IProps {
  patientGuid: string;
  module: ModuleType;
  open: boolean;
  onClose: () => void;
  isInitialTemplateUsed?: boolean;
}

const ClinicalNotesLoggedModal: React.FC<IProps> = (props) => {
  const { patientGuid, module, open, onClose, isInitialTemplateUsed } = props;
  const { t } = useTranslation();

  const { loading, updatePatientNextAppointment } = usePatientNextAppointment({
    patientGuid,
  });

  const pendingAppointmentDetailsRef = useRef<NextAppointment | null>(null);

  const handleAppointmentChange = useCallback(
    (details: NextAppointment | null) => {
      pendingAppointmentDetailsRef.current = details;
    },
    [],
  );

  const updateNextAppointment = useCallback(
    async (details: NextAppointment | null) => {
      const scheduledWithData = details?.getScheduledWithData();
      await updatePatientNextAppointment({
        patientGuid,
        appointmentDetails: details?.appointment
          ? {
              guid: details.appointment.guid,
              ...scheduledWithData,
              appointmentMethod: details.appointment.appointmentMethod,
              scheduledDate: details.prepareDate(
                details.appointment.scheduledDate,
                details.appointment.scheduledTime,
              ),
            }
          : undefined,
      });
      pendingAppointmentDetailsRef.current = null;
    },
    [patientGuid, updatePatientNextAppointment],
  );

  const getPendingAppointmentDetails = useCallback(
    () => pendingAppointmentDetailsRef.current,
    [],
  );

  const {
    defaultValue,
    MonthlyInteractionAlert,
    Alert,
    addLoading,
    narrativeModalOpen,
    isNextAppointmentModalOpen,
    onSubmit,
    onCancelForm,
    toggleOpenNarrativeModal,
    setDefaultValue,
    setIsNextAppointmentModalOpen,
    setIsTemplateBased,
    saveCcmInteractionUpdateFunction,
    setApcmTemplateType,
  } = useLogic({
    patientGuid,
    module,
    onClose,
    updateNextAppointment,
    getPendingAppointmentDetails,
  });
  const [openAppendixCModal, setOpenAppendixCModal] = useState(false);
  const [openIntroCallModal, setOpenIntroCallModal] = useState(false);
  const [openNormalReadingsModal, setOpenNormalReadingsModal] = useState(false);
  const [openDeviceTroubleshootingModal, setOpenDeviceTroubleshootingModal] =
    useState(false);
  const [openNoReadingsModal, setOpenNoReadingsModal] = useState(false);
  const [openMonthlyInteractionCall, setOpenMonthlyInteractionCall] =
    useState(false);
  const [openWrapUpModal, setOpenWrapUpModal] = useState(false);
  const [openCkdModal, toggleOpenCkdModal] = useReducer((v) => !v, false);
  const [openAPCMInitialModal, setOpenAPCMInitialModal] = useReducer(
    (v) => !v,
    false,
  );
  const [openAPCMMonthlyModal, setOpenAPCMMonthlyModal] = useReducer(
    (v) => !v,
    false,
  );
  const { rpmInteractionTime } = useGetRpmInteractionTime({
    patientGuid,
    enabled: true,
  });
  const isMoreThanRpm80Minutes =
    (rpmInteractionTime?.totalTime || 0) > RPM_80_MINUTES;
  const allowApcmMonthlyForm = isMoreThanRpm80Minutes && isInitialTemplateUsed;

  const btnLabel = {
    [ModuleType.RPM]: t('labels.addModuleNote', { module: 'RPM' }),
    [ModuleType.CCM]: t('labels.addModuleNote', { module: 'CCM' }),
    [ModuleType.APCM]: t('labels.addModuleNote', { module: 'APCM' }),
    [ModuleType.ADMIN]: '',
    [ModuleType.RPMCCM]: '',
  } satisfies Record<ModuleType, string>;

  const toggleAppendixCModal = () => setOpenAppendixCModal(!openAppendixCModal);
  const showDeviceTroubleshootingModal =
    openAppendixCModal ||
    openIntroCallModal ||
    openNormalReadingsModal ||
    openDeviceTroubleshootingModal ||
    openNoReadingsModal ||
    openMonthlyInteractionCall ||
    openWrapUpModal;

  const clearDeviceTroubleshootingModals = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`device-troubleshoot_`)) {
        localStorage.removeItem(key);
      }
    });
  };

  const templateButtons = [
    {
      onClick: toggleAppendixCModal,
      module: ModuleType.RPM,
      label: t('controls.generateDeviceTroubleshooting'),
      dataCy: 'useTemplate-button',
    },
    {
      onClick: toggleOpenNarrativeModal,
      module: ModuleType.CCM,
      label: t('controls.useTemplate'),
      dataCy: 'useTemplate-button',
    },
    {
      onClick: toggleOpenCkdModal,
      module: ModuleType.CCM,
      label: t('controls.ckdTemplate'),
      dataCy: 'useCKDTemplate-button',
    },
    {
      onClick: setOpenAPCMInitialModal,
      module: ModuleType.APCM,
      label: t('controls.apcmInitialTemplate'),
      dataCy: 'apcm-initial-template-button',
      disabled: isInitialTemplateUsed,
    },
    {
      onClick: setOpenAPCMMonthlyModal,
      module: ModuleType.APCM,
      label: t('controls.apcmMonthlyTemplate'),
      dataCy: 'apcm-monthly-template-button',
      disabled: !allowApcmMonthlyForm,
    },
  ].filter((btn) => btn.module === module);

  const showNextAppLoading = module !== ModuleType.APCM && loading;

  return (
    <>
      <ClinicalNotesModalForm
        title={btnLabel[module]}
        loading={addLoading || showNextAppLoading}
        onCancelForm={() => {
          onCancelForm();
          clearDeviceTroubleshootingModals();
          pendingAppointmentDetailsRef.current = null;
        }}
        onSubmit={async (values) => {
          try {
            await onSubmit(values);
            pendingAppointmentDetailsRef.current = null;
          } catch (err) {
            debug(err);
          } finally {
            clearDeviceTroubleshootingModals();
          }
        }}
        templateButtons={templateButtons}
        defaultValues={
          defaultValue ? { note: defaultValue, module } : undefined
        }
        open={open}
      />
      {narrativeModalOpen && (
        <CcmClinicalNotesModal
          isOpen={narrativeModalOpen}
          closeModal={toggleOpenNarrativeModal}
          onSubmit={setDefaultValue}
          patientGuid={patientGuid}
        />
      )}
      {showDeviceTroubleshootingModal && (
        <DeviceTroubleshootModals
          patientGuid={patientGuid}
          openMonthlyInteractionCall={openMonthlyInteractionCall}
          toggleOpenMonthlyInteractionCall={setOpenMonthlyInteractionCall}
          openAppendixCModal={openAppendixCModal}
          toggleOpenAppendixCModal={setOpenAppendixCModal}
          openIntroCallModal={openIntroCallModal}
          toggleOpenIntroCallModal={setOpenIntroCallModal}
          openNormalReadingsModal={openNormalReadingsModal}
          toggleOpenNormalReadingsModal={setOpenNormalReadingsModal}
          openDeviceTroubleshootingModal={openDeviceTroubleshootingModal}
          toggleOpenDeviceTroubleshootingModal={
            setOpenDeviceTroubleshootingModal
          }
          openNoReadingsModal={openNoReadingsModal}
          toggleOpenNoReadingsModal={setOpenNoReadingsModal}
          openWrapUpModal={openWrapUpModal}
          toggleOpenWrapUpModal={setOpenWrapUpModal}
          onAppointmentChange={handleAppointmentChange}
          onSaveForm={(data: string) => {
            if (defaultValue) {
              if (openAppendixCModal) {
                setDefaultValue(data);
              } else {
                setDefaultValue(
                  deleteUnnecessaryEmptyLines(defaultValue + data),
                );
              }
            }
            if (!defaultValue) {
              setDefaultValue(data);
            }
            setIsTemplateBased(true);
          }}
        />
      )}
      {!!isNextAppointmentModalOpen && (
        <CcmInteractionStatusModal
          isOpen
          toggle={setIsNextAppointmentModalOpen}
          patientGuid={patientGuid}
          onLaterSend={saveCcmInteractionUpdateFunction}
        />
      )}
      <CkdCcmClinicalModal
        open={openCkdModal}
        toggleOpen={toggleOpenCkdModal}
        onSubmit={setDefaultValue}
        patientGuid={patientGuid}
      />
      <InitialApcmTemplateModal
        open={openAPCMInitialModal}
        onClose={setOpenAPCMInitialModal}
        onSubmit={(v) => {
          setDefaultValue(v);
          setApcmTemplateType('initial');
          setIsTemplateBased(true);
        }}
      />
      <MonthlyApcmTemplateModal
        open={openAPCMMonthlyModal}
        onClose={setOpenAPCMMonthlyModal}
        onSubmit={(v) => {
          setDefaultValue(v);
          setApcmTemplateType('monthly');
          setIsTemplateBased(true);
        }}
      />
      {MonthlyInteractionAlert}
      {Alert}
    </>
  );
};

export default ClinicalNotesLoggedModal;
